'use client';

import React from 'react';
import { AlertTriangle } from 'lucide-react';
import PolicyLayout, {
  PolicySection,
  PolicyList,
  PolicyCallout,
  TocSection,
} from '@/components/PolicyLayout';

const sections: TocSection[] = [
  { id: 'ai-output', title: 'AI Output Disclaimer' },
  { id: 'no-guarantee', title: 'No Job Guarantee' },
  { id: 'third-party-jobs', title: 'Third-Party Jobs' },
  { id: 'automation', title: 'Automation Disclaimer' },
  { id: 'informational', title: 'Informational Purposes' },
  { id: 'professional-advice', title: 'Not Professional Advice' },
];

export default function DisclaimerClient() {
  return (
    <PolicyLayout
      title="Disclaimer"
      description="Important disclaimers about AI outputs, job listings, automation features, and the informational nature of JobCompanion's services."
      lastUpdated="May 18, 2026"
      icon={AlertTriangle}
      iconBg="bg-warning/10"
      iconColor="text-warning"
      sections={sections}
      currentPath="/disclaimer"
    >
      <PolicyCallout type="warning">
        Please read this disclaimer carefully before using JobCompanion. By using the platform, you acknowledge and accept these limitations.
      </PolicyCallout>

      <PolicySection id="ai-output" title="AI Output Disclaimer">
        <p>
          JobCompanion uses artificial intelligence (AI) to generate resume analyses, job match scores, cover letters, application answers, and career recommendations. These outputs are generated by machine learning models and carry inherent limitations:
        </p>
        <PolicyList items={[
          'AI outputs may contain factual errors, outdated information, or contextual inaccuracies',
          'AI-generated cover letters and answers are drafts — they are not guaranteed to be accurate, appropriate, or effective for any specific employer',
          'Job match scores are statistical estimates, not predictions of hiring outcomes',
          'Resume analysis may miss industry-specific signals, cultural context, or nuanced experience',
          'AI recommendations do not account for real-time job market conditions',
        ]} />
        <p>
          <strong>You are solely responsible</strong> for reviewing, editing, and verifying all AI-generated content before submitting it to any employer or third party. JobCompanion is not liable for outcomes resulting from unreviewed AI-generated content.
        </p>
      </PolicySection>

      <PolicySection id="no-guarantee" title="No Job Guarantee">
        <p>
          JobCompanion does not guarantee, promise, or imply that using the platform will result in:
        </p>
        <PolicyList items={[
          'Job interviews or callbacks from employers',
          'Job offers or employment',
          'Improved application success rates',
          'Faster job search timelines',
          'Salary improvements or career advancement',
        ]} />
        <p>
          Job search outcomes depend on many factors entirely outside our control, including employer preferences, market conditions, competition, and timing. We are a tool to assist your search — not a placement service.
        </p>
      </PolicySection>

      <PolicySection id="third-party-jobs" title="Third-Party Job Listings Disclaimer">
        <p>
          Job listings displayed on JobCompanion are sourced from third-party providers including public job boards, company career pages, and API partners. We do not:
        </p>
        <PolicyList items={[
          'Verify the accuracy, legitimacy, or current availability of any job listing',
          'Guarantee that listed positions are still open at the time you view them',
          'Endorse or vet any employer, company, or job posting',
          'Take responsibility for the content of third-party job descriptions',
          'Guarantee that applying through a third-party link will work as expected',
        ]} />
        <p>
          Always verify job listings directly on the employer&apos;s official website before applying. Be cautious of job postings that request payment, personal financial information, or seem too good to be true.
        </p>
        <PolicyCallout type="warning">
          JobCompanion is not responsible for fraudulent job listings sourced from third-party providers. If you encounter a suspicious listing, please report it via our <a href="/contact-grievance" className="text-primary underline">Contact page</a>.
        </PolicyCallout>
      </PolicySection>

      <PolicySection id="automation" title="Automation Disclaimer">
        <p>
          JobCompanion&apos;s automation features are in development and subject to strict limitations. Specifically:
        </p>
        <PolicyList items={[
          'No fully autonomous job application submission is active in the current beta',
          'All AI-assisted workflows require explicit user review and approval before any action is taken',
          'Future auto-apply features will require explicit opt-in and configuration',
          'Automation may interact with third-party websites in ways that could conflict with those sites\' terms of service',
          'We do not guarantee that automation workflows will complete successfully on all job platforms',
          'Automation features may be paused, modified, or removed at any time during beta',
        ]} />
        <p>
          Users are responsible for ensuring their use of automation features complies with the terms of service of any job platform they apply to.
        </p>
      </PolicySection>

      <PolicySection id="informational" title="Informational Purposes Only">
        <p>
          All content on JobCompanion — including AI analyses, job recommendations, career tips, skill gap reports, and application guidance — is provided for <strong>informational purposes only</strong>.
        </p>
        <p>
          This content does not constitute:
        </p>
        <PolicyList items={[
          'Professional career counseling or coaching',
          'Legal advice regarding employment law or contracts',
          'Financial advice regarding salary negotiation',
          'Psychological or mental health guidance',
          'Guarantees of any specific outcome',
        ]} />
      </PolicySection>

      <PolicySection id="professional-advice" title="Not Professional Advice">
        <p>
          JobCompanion is not a licensed career counselor, employment agency, or legal advisor. For professional career guidance, legal employment questions, or mental health support during your job search, please consult qualified professionals in those fields.
        </p>
        <p>
          Questions? Contact us at <a href="/contact-grievance" className="text-primary underline">our contact page</a>.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}
